import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Activity, Beaker, Download, FileText, Pause, Play, RefreshCw, RotateCcw,
  Sparkles, Users, Wallet, TrendingUp, Zap, BadgeDollarSign, Signal, Globe2,
} from "lucide-react";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { AnimatePresence, motion } from "framer-motion";
import api from "../../lib/api";
import { PageHeader, SkeletonGrid, Table, Td } from "../../components/dashboard/Shared";

const PERIODS = [
  { v: "today", l: "Today" },
  { v: "yesterday", l: "Yesterday" },
  { v: "7d", l: "Last 7 Days" },
  { v: "30d", l: "Last 30 Days" },
  { v: "this_month", l: "This Month" },
  { v: "last_month", l: "Last Month" },
  { v: "this_year", l: "This Year" },
  { v: "custom", l: "Custom" },
];

const PROFILES = [
  { v: "CONSERVATIVE", l: "Conservative" },
  { v: "NORMAL", l: "Normal" },
  { v: "AGGRESSIVE", l: "Aggressive" },
  { v: "HYPER_GROWTH", l: "Hyper Growth" },
];

const money = (v, d = 0) =>
  v === null || v === undefined ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const num = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("en-US"));
const pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);

/* Animated number that eases from previous to new value */
const AnimatedNumber = ({ value, format = num, duration = 800 }) => {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);
  useEffect(() => {
    if (value === undefined || value === null) return;
    const from = Number(prevRef.current) || 0;
    const to = Number(value) || 0;
    if (from === to) { setDisplay(to); return; }
    const start = performance.now();
    let raf = 0;
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(step);
      else prevRef.current = to;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span>{format(display)}</span>;
};

const KpiCard = ({ label, value, format, sub, delta, accent = "text-white", icon: Icon, testId }) => (
  <div className="card-dark p-5 fade-up relative overflow-hidden" data-testid={testId}>
    <div className="flex items-start justify-between">
      <p className="label-xs">{label}</p>
      {Icon && <Icon className="w-4 h-4 text-[#7000FF]" />}
    </div>
    <p className={`tabular text-2xl sm:text-3xl mt-2 ${accent}`}>
      <AnimatedNumber value={value} format={format || num} />
    </p>
    <div className="flex items-center gap-2 mt-1">
      {sub && <p className="text-xs text-gray-500">{sub}</p>}
      {delta !== null && delta !== undefined && (
        <span className={`text-xs tabular ${delta >= 0 ? "text-[#00E676]" : "text-[#FF3B30]"}`}>
          {delta >= 0 ? "+" : ""}{Number(delta).toFixed(1)}%
        </span>
      )}
    </div>
  </div>
);

const eventTint = (k) => ({
  signup: "text-[#00F0FF]", deposit: "text-[#00E676]", withdrawal: "text-[#FFD600]",
  mt5_connected: "text-[#7000FF]", kyc_approved: "text-[#00E676]",
  signal_executed: "text-white", profit_share: "text-[#00E676]",
  subscription: "text-[#FFD600]", trade_closed: "text-gray-300",
}[k] || "text-gray-300");

export default function Status() {
  const [settings, setSettings] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [series, setSeries] = useState([]);
  const [tops, setTops] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [feed, setFeed] = useState([]);
  const [feedPaused, setFeedPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const lastEventTsRef = useRef(null);

  const params = useMemo(() => {
    const p = { period };
    if (period === "custom" && customFrom && customTo) {
      p.date_from = customFrom; p.date_to = customTo;
    }
    return p;
  }, [period, customFrom, customTo]);

  const loadAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [s, k, ser, t] = await Promise.all([
        api.get("/status/settings"),
        api.get("/status/kpis", { params }),
        api.get("/status/series", { params: { ...params, period: params.period === "today" ? "30d" : params.period } }),
        api.get("/status/tops"),
      ]);
      setSettings(s.data);
      setKpis(k.data);
      setSeries(ser.data.series || []);
      setTops(t.data);
    } catch (e) {
      if (!silent) toast.error("Failed to load status data");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [params]);

  const loadFeed = useCallback(async () => {
    try {
      const q = {};
      if (lastEventTsRef.current) q.since = lastEventTsRef.current;
      else {
        const d = new Date(Date.now() - 15 * 60 * 1000);
        q.since = d.toISOString();
      }
      q.limit = 25;
      const { data } = await api.get("/status/live-feed", { params: q });
      setFeedPaused(!!data.paused);
      if (data.events?.length) {
        setFeed((prev) => {
          const merged = [...data.events, ...prev];
          const seen = new Set();
          const dedup = merged.filter((e) => (seen.has(e.id) ? false : (seen.add(e.id), true)));
          return dedup.slice(0, 40);
        });
        lastEventTsRef.current = data.events[0].ts;
      }
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-refresh KPIs/series every 60s
  useEffect(() => {
    const id = setInterval(() => loadAll(true), 60_000);
    return () => clearInterval(id);
  }, [loadAll]);

  // Live feed poll every 25s (within the 20-40s range)
  useEffect(() => {
    loadFeed();
    const id = setInterval(loadFeed, 25_000);
    return () => clearInterval(id);
  }, [loadFeed]);

  const patchSettings = async (patch) => {
    try {
      const { data } = await api.patch("/status/settings", patch);
      setSettings(data);
      toast.success("Settings updated");
      loadAll(true);
    } catch { toast.error("Failed to update"); }
  };

  const adminAction = async (path, msg) => {
    setBusy(true);
    try {
      await api.post(path);
      toast.success(msg);
      lastEventTsRef.current = null; setFeed([]);
      await loadAll(true);
      await loadFeed();
    } catch { toast.error("Action failed"); }
    finally { setBusy(false); }
  };

  const downloadFile = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob", params });
      const blob = new Blob([res.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename; link.click();
      URL.revokeObjectURL(link.href);
    } catch { toast.error("Download failed"); }
  };

  const latest = kpis?.latest || {};
  const delta = kpis?.delta || {};
  const sums = useMemo(() => kpis?.sums || {}, [kpis]);
  const chartData = useMemo(() => series.map((s) => ({
    day: s.day?.slice(5) || "",
    users: s.users, aum: s.aum, revenue: s.revenue, pnl: s.todays_pnl,
    signals: s.signals_generated, executed: s.signals_executed,
    win: s.win_rate, mrr: s.mrr,
  })), [series]);

  const claudeVsManual = useMemo(() => [
    { name: "Claude", value: sums.claude_signals || 0, color: "#7000FF" },
    { name: "Manual", value: sums.manual_signals || 0, color: "#00F0FF" },
  ], [sums]);

  return (
    <div className="space-y-6" data-testid="status-page">
      <PageHeader
        title={<span className="flex items-center gap-2">
          <Beaker className="w-6 h-6 text-[#7000FF]" /> STATUS
        </span>}
        sub={<>Executive Business Dashboard<br /><span className="text-xs">Company performance, growth metrics and operational intelligence.</span></>}
      >
        <div className="flex gap-2 items-center">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="bg-[#121212] border border-white/10 text-white rounded px-3 py-2 text-sm"
            data-testid="status-period-select"
          >
            {PERIODS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
          </select>
          {period === "custom" && (
            <>
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                className="bg-[#121212] border border-white/10 text-white rounded px-2 py-2 text-sm" data-testid="status-date-from" />
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                className="bg-[#121212] border border-white/10 text-white rounded px-2 py-2 text-sm" data-testid="status-date-to" />
            </>
          )}
          <button onClick={() => loadAll()} className="btn-secondary flex items-center gap-2" data-testid="status-refresh-btn">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button onClick={() => downloadFile("/status/export/report.pdf", `chartzfx-status-${period}.pdf`)}
            className="btn-primary flex items-center gap-2" data-testid="status-pdf-btn">
            <FileText className="w-4 h-4" /> PDF
          </button>
        </div>
      </PageHeader>

      {/* Paused banner */}
      {settings && !settings.enabled && (
        <div className="card-dark p-4 border-yellow-500/40 border" data-testid="status-paused-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-yellow-400">
              <Pause className="w-4 h-4" /> Dashboard is currently paused. Live activity and daily updates are on hold.
            </div>
            <button onClick={() => patchSettings({ enabled: true })} className="btn-primary text-sm">Resume</button>
          </div>
        </div>
      )}

      {/* KPI Grid */}
      {loading ? <SkeletonGrid n={8} /> : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Total Users" value={latest.users} icon={Users} delta={delta.users} testId="kpi-users" />
          <KpiCard label="Investors" value={latest.investors} icon={Users} delta={delta.investors} testId="kpi-investors" />
          <KpiCard label="AUM" value={latest.aum} format={(v) => money(v)} icon={Wallet} delta={delta.aum} accent="text-[#00F0FF]" testId="kpi-aum" />
          <KpiCard label="MRR" value={latest.mrr} format={(v) => money(v, 0)} icon={BadgeDollarSign} delta={delta.mrr} accent="text-[#00E676]" testId="kpi-mrr" />
          <KpiCard label="ARR" value={latest.arr} format={(v) => money(v, 0)} icon={TrendingUp} accent="text-[#00E676]" testId="kpi-arr" />
          <KpiCard label="Revenue (range)" value={sums.revenue} format={(v) => money(v, 0)} icon={BadgeDollarSign} accent="text-[#00E676]" testId="kpi-revenue" />
          <KpiCard label="Win Rate" value={latest.win_rate} format={(v) => pct(v)} icon={Sparkles} accent="text-[#00E676]" testId="kpi-winrate" />
          <KpiCard label="Profit Factor" value={latest.profit_factor} format={(v) => Number(v).toFixed(2)} icon={Zap} testId="kpi-pf" />
          <KpiCard label="Signals Generated" value={sums.signals_generated} icon={Signal} delta={delta.signals_generated} testId="kpi-signals" />
          <KpiCard label="Signals Executed" value={sums.signals_executed} icon={Signal} testId="kpi-executed" />
          <KpiCard label="Connected MT5" value={latest.connected_mt5} icon={Activity} testId="kpi-mt5" />
          <KpiCard label="Verified KYC" value={latest.verified_kyc} icon={Sparkles} testId="kpi-kyc" />
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-dark p-5" data-testid="chart-users">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-display">Users Growth</h3>
            <span className="text-xs text-gray-500">{series.length} days</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="gUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#7000FF" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#7000FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" stroke="#6B7280" fontSize={10} />
              <YAxis stroke="#6B7280" fontSize={10} />
              <CartesianGrid stroke="#1F2937" strokeDasharray="3 3" />
              <Tooltip contentStyle={{ background: "#0B0B0F", border: "1px solid #1F2937" }} />
              <Area type="monotone" dataKey="users" stroke="#7000FF" fill="url(#gUsers)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="card-dark p-5" data-testid="chart-aum">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-display">AUM Growth</h3>
            <span className="text-xs text-gray-500">USD</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="gAum" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#00F0FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" stroke="#6B7280" fontSize={10} />
              <YAxis stroke="#6B7280" fontSize={10} />
              <CartesianGrid stroke="#1F2937" strokeDasharray="3 3" />
              <Tooltip contentStyle={{ background: "#0B0B0F", border: "1px solid #1F2937" }}
                formatter={(v) => money(v)} />
              <Area type="monotone" dataKey="aum" stroke="#00F0FF" fill="url(#gAum)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="card-dark p-5" data-testid="chart-revenue">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-display">Revenue & MRR</h3>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <XAxis dataKey="day" stroke="#6B7280" fontSize={10} />
              <YAxis stroke="#6B7280" fontSize={10} />
              <CartesianGrid stroke="#1F2937" strokeDasharray="3 3" />
              <Tooltip contentStyle={{ background: "#0B0B0F", border: "1px solid #1F2937" }} formatter={(v) => money(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="revenue" stroke="#00E676" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="mrr" stroke="#FFD600" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="card-dark p-5" data-testid="chart-signals">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-display">Signals Generated vs Executed</h3>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData.slice(-14)}>
              <XAxis dataKey="day" stroke="#6B7280" fontSize={10} />
              <YAxis stroke="#6B7280" fontSize={10} />
              <CartesianGrid stroke="#1F2937" strokeDasharray="3 3" />
              <Tooltip contentStyle={{ background: "#0B0B0F", border: "1px solid #1F2937" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="signals" fill="#7000FF" />
              <Bar dataKey="executed" fill="#00F0FF" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card-dark p-5" data-testid="chart-pnl">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-display">Daily P&L</h3>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData.slice(-30)}>
              <XAxis dataKey="day" stroke="#6B7280" fontSize={10} />
              <YAxis stroke="#6B7280" fontSize={10} />
              <CartesianGrid stroke="#1F2937" strokeDasharray="3 3" />
              <Tooltip contentStyle={{ background: "#0B0B0F", border: "1px solid #1F2937" }} formatter={(v) => money(v)} />
              <Bar dataKey="pnl">
                {chartData.slice(-30).map((_, i) => (
                  <Cell key={i} fill={chartData.slice(-30)[i].pnl >= 0 ? "#00E676" : "#FF3B30"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card-dark p-5" data-testid="chart-source-mix">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-display">Signal Source Mix</h3>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={claudeVsManual} dataKey="value" nameKey="name" outerRadius={78} innerRadius={44} paddingAngle={2}>
                {claudeVsManual.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0B0B0F", border: "1px solid #1F2937" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Live Feed + Top Lists row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-dark p-5 lg:col-span-1" data-testid="live-feed">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-display flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${feedPaused ? "bg-gray-500" : "bg-[#00E676] animate-pulse"}`} />
              Live Activity
            </h3>
            <span className="text-xs text-gray-500">{feed.length} events</span>
          </div>
          <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
            <AnimatePresence initial={false}>
              {feed.map((e) => (
                <motion.div key={e.id}
                  initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                  className="border border-white/5 rounded-md p-3 hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs uppercase tracking-wider ${eventTint(e.kind)}`}>{e.kind.replace("_", " ")}</span>
                    <span className="text-[10px] text-gray-500">{new Date(e.ts).toLocaleTimeString()}</span>
                  </div>
                  <p className="text-sm text-gray-200">{e.msg}</p>
                  {(e.who || e.country) && (
                    <p className="text-xs text-gray-500 mt-1">{e.who}{e.country ? ` · ${e.country}` : ""}</p>
                  )}
                </motion.div>
              ))}
              {!feed.length && (
                <p className="text-sm text-gray-500 text-center py-8">Waiting for events…</p>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          <TopList title="Top Brokers" icon={Wallet} rows={tops?.brokers?.slice(0, 6)} render={(r) => (
            <><Td>{r.name}</Td><Td className="text-right">{money(r.aum)}</Td><Td className="text-right">{r.clients}</Td></>
          )} headers={["Broker", "AUM", "Clients"]} testId="tops-brokers" />
          <TopList title="Top Countries" icon={Globe2} rows={tops?.countries?.slice(0, 6)} render={(r) => (
            <><Td>{r.name}</Td><Td className="text-right">{num(r.users)}</Td><Td className="text-right">{money(r.aum)}</Td></>
          )} headers={["Country", "Users", "AUM"]} testId="tops-countries" />
          <TopList title="Top Clients" icon={Users} rows={tops?.clients?.slice(0, 6)} render={(r) => (
            <><Td>{r.name}</Td><Td>{r.country}</Td><Td className="text-right">{money(r.value)}</Td></>
          )} headers={["Client", "Country", "Value"]} testId="tops-clients" />
          <TopList title="Top Managers" icon={TrendingUp} rows={tops?.managers?.slice(0, 6)} render={(r) => (
            <><Td>{r.name}</Td><Td>{r.country}</Td><Td className="text-right">{money(r.value)}</Td></>
          )} headers={["Manager", "Country", "Value"]} testId="tops-managers" />
        </div>
      </div>

      {/* Admin Controls */}
      <div className="card-dark p-5" data-testid="status-admin-panel">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-display flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#7000FF]" /> Status Settings & Controls
          </h3>
          {settings && (
            <span className="text-xs text-gray-500">
              Started {settings.started_at} · seed {settings.seed} · profile {settings.growth_profile}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="label-xs">Growth Profile</label>
            <select value={settings?.growth_profile || "NORMAL"}
              onChange={(e) => patchSettings({ growth_profile: e.target.value })}
              className="w-full mt-1 bg-[#121212] border border-white/10 text-white rounded px-3 py-2 text-sm"
              data-testid="status-profile-select">
              {PROFILES.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
          </div>
          <div>
            <label className="label-xs">Seed</label>
            <input type="number" min={1} defaultValue={settings?.seed || 42}
              onBlur={(e) => {
                const v = parseInt(e.target.value);
                if (v && v !== settings?.seed) patchSettings({ seed: v });
              }}
              className="w-full mt-1 bg-[#121212] border border-white/10 text-white rounded px-3 py-2 text-sm"
              data-testid="status-seed-input" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!settings?.enabled}
                onChange={(e) => patchSettings({ enabled: e.target.checked })}
                data-testid="status-enabled-toggle" />
              <span className="text-sm text-gray-200">Enabled</span>
            </label>
          </div>
          <div className="flex items-end gap-2">
            {settings?.enabled ? (
              <button onClick={() => adminAction("/status/admin/pause", "Dashboard paused")}
                disabled={busy} className="btn-secondary flex items-center gap-2 text-sm" data-testid="status-pause-btn">
                <Pause className="w-4 h-4" /> Pause
              </button>
            ) : (
              <button onClick={() => adminAction("/status/admin/resume", "Dashboard resumed")}
                disabled={busy} className="btn-primary flex items-center gap-2 text-sm" data-testid="status-resume-btn">
                <Play className="w-4 h-4" /> Resume
              </button>
            )}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <button onClick={() => adminAction("/status/admin/regenerate-today", "Today regenerated")}
            disabled={busy} className="btn-secondary flex items-center gap-2 text-sm" data-testid="regen-today-btn">
            <RefreshCw className="w-4 h-4" /> Regenerate Today
          </button>
          <button onClick={() => adminAction("/status/admin/regenerate-month", "Month regenerated")}
            disabled={busy} className="btn-secondary flex items-center gap-2 text-sm" data-testid="regen-month-btn">
            <RefreshCw className="w-4 h-4" /> Regenerate Month
          </button>
          <button onClick={() => {
            if (!window.confirm("Reset the dashboard database and rebuild the last 120 days of history?")) return;
            adminAction("/status/admin/reset", "Database reset");
          }} disabled={busy} className="btn-secondary flex items-center gap-2 text-sm text-red-400" data-testid="reset-btn">
            <RotateCcw className="w-4 h-4" /> Reset Database
          </button>
          <span className="ml-auto flex flex-wrap gap-2">
            <button onClick={() => downloadFile("/status/export/performance.csv", `performance-${period}.csv`)}
              className="btn-secondary flex items-center gap-2 text-sm" data-testid="csv-performance-btn">
              <Download className="w-4 h-4" /> Performance CSV
            </button>
            <button onClick={() => downloadFile("/status/export/signups.csv", `signups-${period}.csv`)}
              className="btn-secondary flex items-center gap-2 text-sm" data-testid="csv-signups-btn">
              <Download className="w-4 h-4" /> Signups CSV
            </button>
            <button onClick={() => downloadFile("/status/export/deposits.csv", `deposits-${period}.csv`)}
              className="btn-secondary flex items-center gap-2 text-sm" data-testid="csv-deposits-btn">
              <Download className="w-4 h-4" /> Deposits CSV
            </button>
            <button onClick={() => downloadFile("/status/export/signals.csv", `signals-${period}.csv`)}
              className="btn-secondary flex items-center gap-2 text-sm" data-testid="csv-signals-btn">
              <Download className="w-4 h-4" /> Signals CSV
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

const TopList = ({ title, icon: Icon, headers, rows, render, testId }) => (
  <div className="card-dark p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon className="w-4 h-4 text-[#7000FF]" />}
      <h4 className="text-white text-sm font-medium">{title}</h4>
    </div>
    <Table headers={headers}>
      {(rows || []).map((r, i) => (
        <tr key={i} className="border-b border-white/5">{render(r)}</tr>
      ))}
    </Table>
  </div>
);
